import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Bell, CheckCheck } from "lucide-react";

const KIND_LABELS = {
    budget_decided: "Rozpočet",
    invoice_paid: "Platba",
    event_assigned: "Plánovač",
    task_assigned: "Úkol",
    fuel_receipt_missing: "Kniha jízd",
    overdue_invoice: "Po splatnosti",
    warranty_expiring: "Záruka",
    low_margin: "Marže",
    claim_new: "Reklamace",
    piecework_over: "Úkolová práce",
};

export default function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({ items: [], unread: 0 });
    const ref = useRef(null);
    const navigate = useNavigate();

    const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
    useEffect(() => {
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const markRead = async (n) => {
        if (!n.read) {
            await api.post(`/notifications/${n.id}/read`);
            load();
        }
        if (n.link) { setOpen(false); navigate(n.link); }
    };

    const readAll = async () => { await api.post("/notifications/read-all"); load(); };

    return (
        <div className="relative" ref={ref}>
            <button onClick={() => setOpen(!open)} data-testid="notifications-bell"
                    className="relative p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-sm">
                <Bell className="w-5 h-5" />
                {data.unread > 0 && (
                    <span className="absolute top-1 right-1 bg-orange-600 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1 tabular-data">
                        {data.unread > 9 ? "9+" : data.unread}
                    </span>
                )}
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white text-slate-900 border-2 border-slate-900 shadow-2xl z-50" data-testid="notifications-dropdown">
                    <div className="flex items-center justify-between px-4 py-3 border-b-2 border-slate-900">
                        <div className="font-display font-bold uppercase tracking-wider text-sm">Notifikace</div>
                        {data.unread > 0 && (
                            <button onClick={readAll} className="text-xs text-orange-600 hover:text-orange-700 inline-flex items-center gap-1 uppercase tracking-wider">
                                <CheckCheck className="w-3.5 h-3.5" /> Přečíst vše
                            </button>
                        )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {data.items.length === 0 ? (
                            <div className="p-8 text-center text-slate-400 text-sm">Žádné notifikace.</div>
                        ) : data.items.map((n) => (
                            <button key={n.id} onClick={() => markRead(n)}
                                    className={`w-full px-4 py-3 text-left border-b border-slate-100 hover:bg-orange-50/50 ${!n.read ? "bg-orange-50/30" : ""}`}>
                                <div className="flex items-start gap-2">
                                    {!n.read && <div className="w-2 h-2 rounded-full bg-orange-600 mt-1.5"></div>}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[10px] uppercase tracking-wider text-orange-600 font-bold">{KIND_LABELS[n.kind] || n.kind}</div>
                                        <div className="font-medium text-slate-900 text-sm">{n.title}</div>
                                        {n.body && <div className="text-xs text-slate-500 truncate">{n.body}</div>}
                                        <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString("cs-CZ")}</div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
